import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { JsonEditor, githubLightTheme } from 'json-edit-react';
import ShortUniqueId from 'short-unique-id';
import { RootState } from '../../redux';
import { RemoveArrow, SetSelectedTables } from '../../redux/_Dataset/dataset.action';
import { ISelectedTable } from '../../redux/_Dataset/types';
import { getWebApiResponse } from '../../api/webApi';
import JSONCustomButton from '../DataConnection/WebApiDataSource/JSONCustomButton';
import JsonEditorGuide from '../DataConnection/WebApiDataSource/JsonEditorGuide';
import {
  getColumnName,
  getTableName,
  getUniqueTableName,
  isPrimitive,
  isObject,
  isArray,
  getPrimitiveEntries,
  canRenderTable,
} from '../DataConnection/WebApiDataSource/tableValidation';
import { calculateNewNodePosition } from './utils';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import ConfirmDialog from './ConfirmDialog';
import { TAlert } from '../../types/types';
import { validateWebApiTable } from './webApiValidation';

type ColType = 'integer' | 'decimal' | 'text' | 'boolean' | 'date' | 'timestamp';

const TIMESTAMP_FORMATS = ['YYYY-MM-DD HH:mm:ss', 'DD-MM-YYYY HH:mm:ss'];

const DATE_FORMATS = [
  'YYYY-MM-DD', // ISO_LOCAL_DATE: 2025-08-07
  'DD-MM-YYYY',
  'MM/DD/YYYY',
  'YYYY-M-DD',
];

function matchesAnyFormat(value: string, formats: string[]) {
  return formats.some((format) => {
    const parsed = dayjs(value, format, true);
    return parsed.isValid() && parsed.format(format) === value;
  });
}

function isTimestamp(value: string) {
  if (value.includes('T') && dayjs(value).isValid()) return true;
  return matchesAnyFormat(value, TIMESTAMP_FORMATS);
}

function isDate(value: string) {
  return matchesAnyFormat(value, DATE_FORMATS);
}

function getType(value: any): ColType {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string' && isTimestamp(value)) return 'timestamp';
  if (typeof value === 'string' && isDate(value)) return 'date';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'decimal';
  return 'text';
}

function getColumns(data: any, columnName: string): { name: string; type: ColType }[] {
  if (isPrimitive(data)) return [{ name: columnName, type: getType(data) }];
  if (isObject(data)) {
    return getPrimitiveEntries(data as Record<string, unknown>).map(([key, val]) => ({
      name: key,
      type: getType(val),
    }));
  }
  if (isArray(data)) {
    const arr = data as unknown[];
    if (arr.every(isPrimitive)) return [{ name: columnName, type: getType(arr[0]) }];
    if (arr.every(isObject)) {
      const allKeys = [
        ...new Set(
          (arr as Record<string, unknown>[]).flatMap((item) =>
            getPrimitiveEntries(item).map(([key]) => key),
          ),
        ),
      ];
      return allKeys.map((key) => {
        const firstVal = (arr as Record<string, unknown>[])
          .map((item) => item[key])
          .find((value) => value !== undefined && value !== null);
        return { name: key, type: getType(firstVal) };
      });
    }
  }
  return [];
}

interface IWebApiInfo {
  onTableUncheck: (tableIds: string[]) => void;
}

/**
 *
 * @param param0 onTableUncheck (function that filters the tables i.e. removes the selected table which is just unselected. This function is defined in NewDataset.tsx and EditDataset.tsx)
 * @returns second section of sidebar for web api connection. It involves json view and custom buttons to check or uncheck and preview(icons)
 */
function WebApiInfo({ onTableUncheck }: IWebApiInfo) {
  const { selectedConnection, selectedTables, canEdit, arrows } = useSelector(
    (state: RootState) => ({
      selectedConnection: state.dataset.connections.find(
        (connection) => connection.id === state.dataset.selectedConnectionId,
      ),
      selectedTables: state.dataset.selectedTables,
      canEdit: state.dataset.canEdit,
      arrows: state.dataset.arrows,
    }),
  );
  const dispatch = useDispatch();
  const uid = new ShortUniqueId({ length: 4, dictionary: 'alpha' });

  const [responseData, setResponseData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedTablePathsToRemove, setSavedTablePathsToRemove] = useState<string[]>([]);

  /**
   * @returns jsonPath from custom query
   */
  const getPathKey = (customQuery: string): string => {
    try {
      const parsed = JSON.parse(customQuery);
      if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.jsonPath)) {
        return JSON.stringify(parsed.jsonPath);
      }
    } catch {}
    return customQuery;
  };

  // selected node path
  const selectedNodePaths = useMemo(() => {
    if (!selectedConnection) return new Set<string>();
    const paths: string[] = [];

    for (const table of selectedTables) {
      if (table.connectionId !== selectedConnection.id) continue;
      if (!table.isWebApi) continue;
      if (!table.customQuery) continue;
      paths.push(getPathKey(table.customQuery));
    }
    return new Set(paths);
  }, [selectedTables, selectedConnection?.id]);

  // Fetch API response when connection changes
  useEffect(() => {
    if (!selectedConnection || selectedConnection.fileType !== 'webapi') return;
    setResponseData(null);
    const fetchResponse = async () => {
      setLoading(true);
      const res = await getWebApiResponse(selectedConnection.id);
      if (res.isError) {
        setAlert({ severity: 'error', message: res.message ?? 'Failed to fetch API response' });
      } else {
        setResponseData(res.data);
      }
      setLoading(false);
    };
    fetchResponse();
  }, [selectedConnection?.id]);

  const invalidTables = useMemo(() => {
    if (!responseData || !selectedConnection) return new Set<string>();

    const invalid = new Set<string>();

    selectedTables.forEach((t) => {
      if (t.connectionId !== selectedConnection.id || !t.isWebApi) return;

      // Use the imported utility function
      const isValid = validateWebApiTable(t, responseData);

      if (!isValid) {
        invalid.add(t.id);
      }
    });

    return invalid;
  }, [selectedTables, responseData, selectedConnection?.id]);

  useEffect(() => {
    if (!responseData || loading) return;

    if (invalidTables.size > 0) {
      setAlert({
        severity: 'warning',
        message: 'Some tables are no longer valid. Please reselect tables from Select data nodes.',
      });
    } else {
      setAlert(null);
    }
  }, [invalidTables, responseData]);

  const handleSetSelectedTablePaths: React.Dispatch<React.SetStateAction<Set<string>>> =
    useCallback(
      (updater) => {
        if (!selectedConnection || !responseData || !canEdit) return;
        const newPaths = typeof updater === 'function' ? updater(selectedNodePaths) : updater;

        const added = [...newPaths].filter((path) => !selectedNodePaths.has(path));
        const removed = [...selectedNodePaths].filter((path) => !newPaths.has(path));

        let newSelectedTables = [...selectedTables];

        // Add new tables
        for (const pathKey of added) {
          const path: (string | number)[] = JSON.parse(pathKey);
          const rawPath = path.length > 0 && path[0] === 'response' ? path.slice(1) : path;
          let value = responseData;
          for (const key of rawPath) {
            value = value?.[key];
          }
          const namesInUse = new Set(newSelectedTables.map((newtable) => newtable.table));
          const tableName = getUniqueTableName(getTableName(rawPath, value), namesInUse);

          const colName = getColumnName(rawPath);
          const columns = getColumns(value, colName);

          const normalizedPath = path;

          // Build tablePath: strip trailing primitive field name if the node is a single primitive
          const lastSegment = normalizedPath[normalizedPath.length - 1];
          const isPrimitiveNode =
            normalizedPath.length > 0 &&
            typeof lastSegment === 'string' &&
            columns.length === 1 &&
            columns[0].name === lastSegment;
          const tablePath = isPrimitiveNode ? normalizedPath.slice(0, -1) : normalizedPath;
          const pathToArray = (p: (string | number)[]): string[] =>
            p.map((segment) => (typeof segment === 'number' ? `#${segment}` : String(segment)));

          const newTable: ISelectedTable = {
            id: uid(),
            connectionId: selectedConnection.id,
            isFlatFile: false,
            flatFileId: '',
            database: '',
            schema: '',
            table: tableName,
            customQuery: JSON.stringify({
              jsonPath: normalizedPath,
              tablePath: pathToArray(tablePath),
              columns: columns.map((col) => ({ fieldName: col.name, dataType: col.type })),
            }),
            connectionName: selectedConnection.label,
            vendor: 'webapi',
            isCustomQuery: false,
            isNewTable: true,
            isSelected: true,
            isWebApi: true,
            tablePositionX: calculateNewNodePosition(newSelectedTables.length)?.x ?? 0,
            tablePositionY: calculateNewNodePosition(newSelectedTables.length)?.y ?? 0,
            alias: tableName,
            columns: columns.map((col) => ({
              uid: uid(),
              columnName: col.name,
              dataType: col.type,
            })),
            order: newSelectedTables.length + 1,
            isDuplicated: false,
          };
          newSelectedTables = [...newSelectedTables, newTable];
        }

        const savedPathsToConfirm: string[] = [];

        for (const pathKey of removed) {
          const tableToRemove = newSelectedTables.find(
            (table) =>
              table.connectionId === selectedConnection.id &&
              table.isWebApi &&
              !!table.customQuery &&
              getPathKey(table.customQuery) === pathKey,
          );
          if (!tableToRemove) continue;
          if (!tableToRemove.isNewTable) {
            savedPathsToConfirm.push(pathKey);
            continue;
          }
          const arrowIdsToDelete = arrows
            .filter(
              (arrow) => arrow.startId === tableToRemove.id || arrow.endId === tableToRemove.id,
            )
            .map((arrow) => arrow.arrowId);
          if (arrowIdsToDelete.length > 0) dispatch(RemoveArrow(arrowIdsToDelete));
          onTableUncheck([tableToRemove.id]);
          newSelectedTables = newSelectedTables.filter((table) => table.id !== tableToRemove.id);
        }

        dispatch(SetSelectedTables(newSelectedTables));

        if (savedPathsToConfirm.length > 0) {
          setSavedTablePathsToRemove(savedPathsToConfirm);
          setConfirmOpen(true);
        }
      },
      [
        selectedConnection,
        responseData,
        selectedNodePaths,
        selectedTables,
        arrows,
        canEdit,
        dispatch,
      ],
    );

  function handleRemoveTable() {
    const tables = selectedTables;
    const arrowsSnapshot = arrows;
    const savedTablesToRemove = tables.filter(
      (table) =>
        table.connectionId === selectedConnection!.id &&
        table.isWebApi &&
        savedTablePathsToRemove.includes(getPathKey(table.customQuery ?? '')),
    );
    const arrowIdsToDelete = arrowsSnapshot
      .filter((arrow) =>
        savedTablesToRemove.some((table) => table.id === arrow.startId || table.id === arrow.endId),
      )
      .map((arrow) => arrow.arrowId);
    if (arrowIdsToDelete.length > 0) dispatch(RemoveArrow(arrowIdsToDelete));
    onTableUncheck(savedTablesToRemove.map((table) => table.id));
    dispatch(
      SetSelectedTables(
        tables.filter(
          (table) => !savedTablesToRemove.some((tableToRemove) => tableToRemove.id === table.id),
        ),
      ),
    );
    setSavedTablePathsToRemove([]);
  }

  if (!selectedConnection || selectedConnection.fileType !== 'webapi') return null;

  return (
    <Box
      sx={{
        width: '21rem',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: '1px solid #e0e0e0',
        paddingLeft: '8px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          background: '#80808020',
          borderBottom: '1px solid #e0e0e0',
          gap: 0.5,
        }}
      >
        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#484848' }}>
          Select data nodes
        </span>
        <JsonEditorGuide />
      </Box>

      {loading ? (
        <Stack spacing={1} sx={{ p: 1 }}>
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="85%" />
        </Stack>
      ) : responseData ? (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'auto',
            '& .jer-editor-container': {
              width: 'auto !important',
              minWidth: '0 !important',
              maxWidth: '100% !important',
            },
            '& .jer-value-string, & .jer-value-number, & .jer-value-boolean, & .jer-value-null': {
              whiteSpace: 'nowrap !important',
            },
            '&::-webkit-scrollbar': { width: '5px', height: '5px' },
            '&::-webkit-scrollbar-thumb': { background: '#b2b2b2' },
          }}
        >
          <JsonEditor
            data={responseData}
            restrictEdit={true}
            restrictDelete={true}
            restrictAdd={true}
            enableClipboard={false}
            showArrayIndices={false}
            theme={githubLightTheme}
            rootFontSize="9px"
            collapse={1}
            customButtons={[
              {
                Element: (props) => (
                  <JSONCustomButton
                    {...props}
                    selectedNodePaths={selectedNodePaths}
                    setSelectedNodePaths={handleSetSelectedTablePaths}
                  />
                ),
                onClick: () => {},
              },
            ]}
          />
        </Box>
      ) : (
        <Typography sx={{ fontSize: '11px', p: 1, color: 'text.secondary' }}>
          No response data available.
        </Typography>
      )}

      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Remove Table?"
        message="Removing this table may cause issues in the dependant playbooks of this dataset. Are you sure?"
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={() => {
          handleRemoveTable();
          setConfirmOpen(false);
        }}
        onCancel={() => {
          setSavedTablePathsToRemove([]);
          setConfirmOpen(false);
        }}
      />
    </Box>
  );
}

export default WebApiInfo;
